/**
 * UsersTab - World-Class Minimalist Design
 * User management with clean, elegant UI
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, UserPlus, Edit2, Mail, Shield, MoreHorizontal } from 'lucide-react';

const translations = {
  en: {
    users: 'Users',
    subtitle: 'Manage your team members',
    searchUsers: 'Search by name or email...',
    allRoles: 'All roles',
    admin: 'Admin',
    user: 'User',
    inviteUser: 'Invite User',
    name: 'Name',
    role: 'Title / Role',
    team: 'Team',
    actions: 'Actions',
    edit: 'Edit',
    noTeam: 'No team',
    noUsersFound: 'No users found',
    noUsersDesc: 'Try adjusting your search or filters',
    systemAdmin: 'Admin'
  },
  no: {
    users: 'Brukere',
    subtitle: 'Administrer teammedlemmer',
    searchUsers: 'Søk etter navn eller e-post...',
    allRoles: 'Alle roller',
    admin: 'Admin',
    user: 'Bruker',
    inviteUser: 'Inviter bruker',
    name: 'Navn',
    role: 'Tittel / Rolle',
    team: 'Team',
    actions: 'Handlinger',
    edit: 'Rediger',
    noTeam: 'Ingen team',
    noUsersFound: 'Ingen brukere funnet',
    noUsersDesc: 'Prøv å justere søket eller filtrene',
    systemAdmin: 'Admin'
  }
};

interface UsersTabProps {
  users: any[];
  teams: any[];
  onInvite: () => void;
  onEdit: (user: any) => void;
  onRefresh: () => void;
  language?: string;
}

const UsersTab: React.FC<UsersTabProps> = ({ users, teams, onInvite, onEdit, onRefresh, language = 'no' }) => {
  const t = translations[language] || translations.no;
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role: string, jobTitle?: string) => {
    const getJobTitleStyle = (title: string) => {
      switch (title) {
        case 'Managing Partner':
          return 'bg-amber-50 text-amber-700 border-amber-200';
        case 'Senior Partner':
        case 'Partner':
          return 'bg-violet-50 text-violet-700 border-violet-200';
        case 'Senior Associate':
        case 'Associate':
          return 'bg-blue-50 text-blue-700 border-blue-200';
        case 'Junior Associate':
          return 'bg-sky-50 text-sky-700 border-sky-200';
        default:
          return 'bg-slate-50 text-slate-600 border-slate-200';
      }
    };

    return (
      <div className="flex flex-col gap-1.5">
        {jobTitle && (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${getJobTitleStyle(jobTitle)}`}>
            {jobTitle}
          </span>
        )}
        {role === 'ADMIN' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
            <Shield className="w-3 h-3" />
            {t.systemAdmin}
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900">{t.users}</h2>
        <p className="text-slate-500 text-sm mt-1">{t.subtitle}</p>
      </div>

      {/* Search & Actions */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchUsers}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-slate-900 transition-all"
        >
          <option value="all">{t.allRoles}</option>
          <option value="ADMIN">{t.admin}</option>
          <option value="USER">{t.user}</option>
        </select>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onInvite}
          className="ml-auto px-5 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium text-sm flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          {t.inviteUser}
        </motion.button>
      </div>

      {/* Users List */}
      <div className="space-y-2">
        {filteredUsers.map((user, index) => {
          const userTeam = teams.find(t => t.id === user.team_id);
          return (
            <motion.div 
              key={user.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="group flex items-center gap-4 px-5 py-4 bg-slate-50/50 hover:bg-slate-50 rounded-xl cursor-pointer transition-all border border-transparent hover:border-slate-200"
            >
              {/* Avatar & Info */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-medium text-sm overflow-hidden bg-gradient-to-br from-slate-600 to-slate-800 flex-shrink-0">
                  {user.profile_picture_url ? (
                    <img src={user.profile_picture_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.name?.charAt(0) || '?'
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{user.name}</p>
                  <div className="flex items-center gap-1.5 text-sm text-slate-500">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                </div>
              </div>
              
              {/* Role Badge */}
              <div className="w-36">
                {getRoleBadge(user.role, user.job_title)}
              </div>
              
              {/* Team */}
              <div className="w-32">
                {userTeam ? (
                  <span className="text-sm text-slate-700">{userTeam.name}</span>
                ) : (
                  <span className="text-sm text-slate-400">{t.noTeam}</span>
                )}
              </div>
              
              {/* Actions */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onEdit(user)}
                className="p-2 text-slate-400 hover:text-slate-900 hover:bg-white rounded-lg transition-all opacity-0 group-hover:opacity-100"
              >
                <Edit2 className="w-4 h-4" />
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-1">{t.noUsersFound}</h3>
          <p className="text-slate-500 text-sm">{t.noUsersDesc}</p>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
